import AppConfig from '@config';
import { OpertoTheme } from '@operto/ui';
import { ReactNode, useEffect, useState } from 'react';
import {
  Dialog,
  Modal,
  ModalOverlay,
  Sheet,
  SheetBackdrop,
  SheetContent,
  SheetOverlay,
} from 'react-aria-components';
import './ActionSheet.css';

export type ActionSheetProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  testID?: string;
  adaptive?: boolean;
  hideDragIndicator?: boolean;
  isScrollable?: boolean;
  backgroundColor?: string;
};

export const ActionSheet = ({
  children,
  isOpen,
  onClose,
  testID,
  adaptive = false,
  hideDragIndicator = false,
  isScrollable = false,
  backgroundColor = OpertoTheme.containerAccentBGColor,
}: ActionSheetProps) => {
  const [isMobile, setIsMobile] = useState(AppConfig.Styles.isMobile);
  useEffect(() => {
    const updateSize = () => setIsMobile(AppConfig.Styles.isMobile());
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const isDialog = adaptive && !isMobile;
  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };
  // Older browsers without scrollend cannot complete React Aria's sheet
  // transitions. Keep an accessible, bottom-positioned modal in that case.
  const supportsSwipe =
    typeof document !== 'undefined' && 'onscrollend' in document;
  const content = (
    <>
      {!isDialog && !hideDragIndicator && (
        <div className="operto-sheet-handle" aria-hidden="true" />
      )}
      <div
        className="operto-sheet-body"
        data-scrollable={isScrollable || undefined}>
        {children}
      </div>
    </>
  );

  if (isDialog || !supportsSwipe) {
    return (
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={!!onClose}
        isKeyboardDismissDisabled={!onClose}
        className="operto-modal-overlay"
        data-placement={isDialog ? 'center' : 'bottom'}>
        <Modal className="operto-modal" style={{ backgroundColor }}>
          <Dialog data-testid={testID} className="operto-sheet-content">
            {content}
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }

  return (
    <SheetOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      preventDismissal={!onClose}
      position="bottom"
      className="operto-sheet-overlay">
      <SheetBackdrop className="operto-sheet-backdrop" />
      <Sheet className="operto-sheet" style={{ backgroundColor }}>
        <SheetContent data-testid={testID} className="operto-sheet-content">
          {content}
        </SheetContent>
      </Sheet>
    </SheetOverlay>
  );
};
